/**
 * Created by Jie Gao on 2021-03-04.
 */
import {WinPresentationController} from "@nolimitcity/slot-game/bin/core/winpresentation/WinPresentationController";
import {GGReelController} from "../../reel/GGReelController";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GGParsedGameData} from "../../server/data/GGParsedGameData";
import {TimelineLite, TweenLite} from "gsap";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GGGameModuleConfig} from "../../GGGameModuleConfig";
import {GGSoundConfig} from "../../GGSoundConfig";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GGGameConfig} from "../../GGGameConfig";
import {GGHelper} from "../../utils/Helper";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {GGReelSymbol} from "../../symbols/GGReelSymbol";
import {FontStyleConfig} from "../../config/FontStyleConfig";
import {GGReelSymbolW} from "../../symbols/GGReelSymbolW";

export class DeadWildTransform extends WinPresentationController {
    private _timeline:TimelineLite | null;
    private _dwTransform:boolean[];
    private _evaluatedArea:string[][];
    private _deadWildPos:[number, number, number, StateReelSymbol][];//[posX, posY, newSymbol]

    constructor(indexes:[number, number]) {
        super(indexes, {name : "DeadWildTransform", skippable : false});
    }

    protected hasWin():boolean {
        // #576
        if (GGGameConfig.instance.FAULTY_WIN_CAP) {
            return false;
        }

        let hasWild:boolean = !((<GGParsedGameData>this._gameData)!.dwTransform == null);
        return hasWild;
    }

    protected parseFeatureGameData(data:GGParsedGameData):void {
        this._deadWildPos = [];
        this._dwTransform = [];
        this._evaluatedArea = data.evaluatedArea;
        if(data.dwTransform) {
            this._dwTransform = data.dwTransform
            this._dwTransform.forEach((isWild:boolean, reel:number) => {
                if(isWild) {
                    data.evaluatedArea[reel].forEach((sym:string, symId:number) => {
                        if(sym[0] === "W" && sym.length > 1 && data.reels[reel][symId] === "Z") {
                            const multiplier:number = parseInt(sym.substring(1));
                            const symMultiplier:number = isNaN(multiplier) ? 0 : multiplier;
                            this._deadWildPos.push([reel, symId, symMultiplier, (<GGReelController>Reels.getReel(reel)).addTransformedSymbol(sym)]);
                        }
                    });
                }
            });
        }
    }

    private switchSymbols():void {
        this._deadWildPos.forEach((pos:[number, number, number, StateReelSymbol]) => {
            (<GGReelController>Reels.getReel(pos[0])).removeTransformedSymbol(pos[3], pos[1]);
        });
    }

    protected startWinPresentation() {
        this._timeline = new TimelineLite({paused : true});
        this._timeline.add(() => {
            GGGameModuleConfig.soundPlayer.playSoundEffect(GGSoundConfig.instance.DEADWILD_GW);
            GGGameModuleConfig.betWaysOnReel.updateBetWays((<GGParsedGameData>this._gameData)!.totalNumberOfWays[5])
        }, 0);
        this._deadWildPos.forEach((pos:[number, number, number, StateReelSymbol]) => {
            const transformAnimations:TimelineLite = new TimelineLite();
            const sym:StateReelSymbol = Reels.getSymbol(pos[0], pos[1])!;
            const multiplier = pos[2];
            const fs:number = 0.033;
            const hasMultiplier:boolean = multiplier > 3;
            const animationScale:number = MathHelper.roundToDecimals((<GGReelSymbol>sym).symHeight / 181, 2);
            const animation = GGHelper.createSpineAnimation("DW_GW", animationScale);
            const newSym:StateReelSymbol = pos[3];
            (<GGReelSymbolW>newSym).hideMultiplier();

            transformAnimations.add([
                () => {
                    StageManager.getLayer(GGGameConfig.instance.LAYERS.GOBLINS.name).addChild(animation);
                    animation.position = GGHelper.copyLocalPosition(animation, sym)
                    animation.position.x+=3;
                    animation.position.y+=5;
                    animation.state.setAnimation(0, "transform", false);
                    animation.visible = true;
                },
                new TweenLite(this, 1.6, {alpha : 1})
            ], 0);

            if(hasMultiplier) {
                const wildMultiplierContainer = this.createWildMultiplier(multiplier);
                const multiplierEffect = GGHelper.createSpineAnimation("DW_GW_Multi", animationScale);
                transformAnimations.add([
                    () => {
                        StageManager.getLayer(GGGameConfig.instance.LAYERS.GOBLINS.name).addChild(multiplierEffect);
                        StageManager.getLayer(GGGameConfig.instance.LAYERS.GOBLINS.name).addChild(wildMultiplierContainer);
                        wildMultiplierContainer.position = GGHelper.copyLocalPosition(wildMultiplierContainer, newSym);
                        multiplierEffect.position = GGHelper.copyLocalPosition(multiplierEffect, newSym);
                        wildMultiplierContainer.visible = true;
                        multiplierEffect.state.setAnimation(0, "Multi_Effect", false);
                        multiplierEffect.visible = true;
                    },
                    TweenLite.fromTo(wildMultiplierContainer.scale, fs * 3, {x : 5, y : 5}, {x : 0.9, y : 0.9})
                ], 1.5);
                transformAnimations.add(TweenLite.to(wildMultiplierContainer.scale, fs * 2, {
                    x : animationScale,
                    y : animationScale
                }), 1.5 + 3 * fs);
                transformAnimations.add(() => (<GGReelSymbolW>newSym).fadeInMultiplier(), 1.5+ 3 * fs);
                transformAnimations.add(() => wildMultiplierContainer.destroy(), 1.5 + 5 * fs);
            }
            transformAnimations.add(() => (<GGReelController>Reels.getReel(pos[0])).removeTransformedSymbol(newSym, pos[1]), 0.65);
            transformAnimations.add(() => animation.destroy(), 1.5 + 5 * fs);
            this._timeline!.add(transformAnimations, 0);
        });
        this._timeline.add(() => this.finish());
        this._timeline.play();
    }

    private createWildMultiplier(multiplier:number):PIXI.Sprite {
        const multiplierNumber:number = GGGameConfig.instance.MULTIPLIER[multiplier].multiplier;
        const wildMultiplierContainer:PIXI.Sprite = new PIXI.Sprite();
        wildMultiplierContainer.anchor.set(0.5, 0.5);
        wildMultiplierContainer.position.set(0, 0);
        wildMultiplierContainer.visible = false;

        const wildMultiplier:PIXI.BitmapText = new PIXI.BitmapText("", FontStyleConfig.WildMultiplier);
        const isEleven:boolean= multiplier === 11;
        wildMultiplier.letterSpacing = isEleven?-60:-40;
        wildMultiplier.text = `x ${isEleven?" ":""}${multiplierNumber}`;
        wildMultiplier.position.set(-wildMultiplier.width * 0.5, 0);

        wildMultiplierContainer.addChild(wildMultiplier);
        wildMultiplierContainer.scale.set(0, 0);
        return wildMultiplierContainer;
    }

    protected stopWinPresentation(isAborted:boolean) {
        if(isAborted) {
            GGGameModuleConfig.betWaysOnReel.updateBetWays((<GGParsedGameData>this._gameData)!.totalNumberOfWays[5]);
            this.switchSymbols();
            if(this._timeline) {
                if(this._timeline.isActive()) {
                    this._timeline.pause();
                }
                this._timeline.kill();
            }
        }
        this._timeline = null;

    }
}